import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { supabase } from "@/lib/supabase";

import CalendarFooter from "@/components/hero/calendar/CalendarFooter";

import { format } from "date-fns";
import { formatDate } from "@/utils/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	Eye,
	EyeOff,
	MapPin,
	Calendar,
} from "lucide-react";
import { motion } from "framer-motion";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";

import "react-day-picker/dist/style.css";

// Google Maps types are declared in LandingPage.tsx

interface ParentSignupData {
	fullName: string;
	email: string;
	password: string;
	passwordConfirmation: string;
	providerName: string;
	providerAddress: string;
	providerPlaceId: string;
	providerCity?: string;
	providerState?: string;
	providerZip?: string;
	providerStreetAddress?: string;
	providerTypes?: string[];
	dueDate: string;
}

const ParentSignupPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const [showDatePicker, setShowDatePicker] = useState(false);
	const [currentDate, setCurrentDate] = useState(new Date());
	const datePickerRef = useRef<HTMLDivElement>(null);
	const [DatePickerButtonValue, setDatePickerButtonValue] = useState("Select Due Date");

	const defaultClassNames = getDefaultClassNames();

	// Get pre-filled data from landing page or sessionStorage
	const getPrefilledData = (): { providerName: string; dueDate: string } => {
		// First try to get from location state (if coming from landing page)
		if (location.state?.searchData) {
			const { providerName, dueDate } = location.state.searchData;
			return {
				providerName:
					providerName && providerName !== "Provider from profile"
						? providerName
						: "",
				dueDate: dueDate && dueDate !== "Due date from profile" ? dueDate : "",
			};
		}

		// Then try sessionStorage (if coming from search results)
		const storedData = sessionStorage.getItem("parentSearchData");
		if (storedData) {
			try {
				const parsedData = JSON.parse(storedData);
				return {
					providerName:
						parsedData.providerName &&
							parsedData.providerName !== "Provider from profile"
							? parsedData.providerName
							: "",
					dueDate:
						parsedData.dueDate && parsedData.dueDate !== "Due date from profile"
							? parsedData.dueDate
							: "",
				};
			} catch (error) {
				return { providerName: "", dueDate: "" };
			}
		}

		// Return empty strings if no data is available
		return { providerName: "", dueDate: "" };
	};

	const [formData, setFormData] = useState<ParentSignupData>({
		fullName: "",
		email: "",
		password: "",
		passwordConfirmation: "",
		providerName: getPrefilledData().providerName,
		providerAddress: "",
		providerPlaceId: "",
		providerCity: "",
		providerState: "",
		providerZip: "",
		providerStreetAddress: "",
		providerTypes: [],
		dueDate: getPrefilledData().dueDate,
	});

	const handleInputChange = (field: keyof ParentSignupData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		setError(null); // Clear errors when user starts typing
	};

	const validateForm = (): boolean => {
		if (!formData.fullName.trim()) {
			setError("Full name is required");
			return false;
		}

		if (!formData.email.trim()) {
			setError("Email is required");
			return false;
		}

		if (!formData.email.includes("@")) {
			setError("Please enter a valid email address");
			return false;
		}

		if (formData.password.length < 6) {
			setError("Password must be at least 6 characters long");
			return false;
		}

		if (formData.password !== formData.passwordConfirmation) {
			setError("Passwords do not match");
			return false;
		}

		if (!formData.providerName.trim()) {
			setError("Healthcare provider is required");
			return false;
		}

		if (!formData.dueDate) {
			setError("Due date is required");
			return false;
		}

		return true;
	};

	const handleDayPickerSelect = (date: Date | undefined) => {
		if (!date) {
			setCurrentDate(new Date());
			setFormData((prev) => ({ ...prev, dueDate: "Select due date" }));
			setDatePickerButtonValue("Select Due Date");
		} else {
			setCurrentDate(date);
			setFormData((prev) => ({ ...prev, dueDate: formatDate(date) }));
			setShowDatePicker(false);
			setDatePickerButtonValue(format(date, "MMMM d, yyyy"));
		}
	}

	// Close date picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				datePickerRef.current &&
				!datePickerRef.current.contains(event.target as Node)
			) {
				setShowDatePicker(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const providerNameRef = useRef<HTMLInputElement>(null);
	const { place: providerPlace } = usePlacesAutocomplete({
		inputRef: providerNameRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "formatted_address", "name", "types", "geometry", "address_components"],
	});

	useEffect(() => {
		if (!providerPlace?.place_id || !providerPlace.name) return;
		let providerCity = "";
		let providerState = "";
		let providerZip = "";
		let providerStreetAddress = "";
		if (providerPlace.address_components) {
			for (const component of providerPlace.address_components) {
				const types = component.types;
				if (types.includes("locality")) providerCity = component.long_name;
				else if (types.includes("administrative_area_level_1")) providerState = component.short_name;
				else if (types.includes("postal_code")) providerZip = component.long_name;
				else if (types.includes("street_number") || types.includes("route")) providerStreetAddress += component.long_name + " ";
			}
		}
		setFormData((prev) => ({
			...prev,
			providerName: providerPlace.name || "",
			providerAddress: providerPlace.formatted_address || "",
			providerPlaceId: providerPlace.place_id || "",
			providerCity: providerCity.trim(),
			providerState: providerState.trim(),
			providerZip: providerZip.trim(),
			providerStreetAddress: providerStreetAddress.trim(),
			providerTypes: providerPlace.types || [],
		}));
	}, [providerPlace]);

	const handleSignup = async () => {
		if (!validateForm()) return;

		setLoading(true);
		setError(null);

		try {
			// Call the parent signup edge function
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-parent-user`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						fullName: formData.fullName,
						email: formData.email,
						password: formData.password,
						passwordConfirmation: formData.passwordConfirmation,
						birthLocation: formData.providerName, // Edge function expects birthLocation
						providerName: formData.providerName,
						providerAddress: formData.providerAddress,
						providerPlaceId: formData.providerPlaceId,
						providerCity: formData.providerCity,
						providerState: formData.providerState,
						providerZip: formData.providerZip,
						providerStreetAddress: formData.providerStreetAddress,
						providerTypes: formData.providerTypes,
						dueDate: formData.dueDate,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Signup failed");
			}

			setSuccess(
				"Account created successfully! Redirecting to your dashboard...",
			);

			// Clear stored data
			sessionStorage.removeItem("parentSearchData");
			sessionStorage.removeItem("selectedDoulaId");

			// Sign in the user
			const { error: signInError } = await supabase.auth.signInWithPassword({
				email: formData.email,
				password: formData.password,
			});

			if (signInError) {
				throw new Error(
					"Signup successful but automatic sign-in failed. Please sign in manually.",
				);
			}

			// Redirect based on stored data
			setTimeout(() => {
				// Priority 1: If they came from a doula profile, go back there
				const selectedDoulaId = sessionStorage.getItem("selectedDoulaId");
				if (selectedDoulaId) {
					navigate(`/doula-profile/${selectedDoulaId}`);
					return;
				}

				// Priority 2: If they came from search results, go back to search
				if (
					location.state?.redirectTo === "/search-doulas" ||
					sessionStorage.getItem("parentSearchData")
				) {
					navigate("/search-doulas");
					return;
				}

				// Priority 3: If they came from landing page search, go to search
				if (location.state?.searchData) {
					navigate("/search-doulas");
					return;
				}

				// Priority 4: Default to parent dashboard
				navigate("/parent-dashboard");
			}, 2000);
		} catch (error: string | any) {
			setError(error.message || "An error occurred during signup");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 flex items-center justify-center py-20 px-4">
			<div className="w-full max-w-6xl">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Form */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						className="flex justify-center lg:justify-start"
					>
						<Card className="glass-card border-0 shadow-xl w-full max-w-md">
							<CardHeader className="text-center pb-4">
								<CardTitle className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[32px]">
									Create Your Account
								</CardTitle>
								<p className="text-black font-karla leading-relaxed lg:text-xl lg:leading-8">
									Complete your profile to connect with doulas
								</p>
							</CardHeader>

							<CardContent className="space-y-4">
								{error && (
									<Alert variant="destructive">
										<AlertDescription>{error}</AlertDescription>
									</Alert>
								)}

								{success && (
									<Alert>
										<AlertDescription>{success}</AlertDescription>
									</Alert>
								)}

								<div className="space-y-3">
									{/* Full Name */}
									<div>
										<Label
											htmlFor="fullName"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Full Name
										</Label>
										<Input
											id="fullName"
											type="text"
											placeholder="Enter your full name"
											value={formData.fullName}
											onChange={(e) =>
												handleInputChange("fullName", e.target.value)
											}
											className="pl-3 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
										/>
									</div>

									{/* Email */}
									<div>
										<Label
											htmlFor="email"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Email Address
										</Label>
										<Input
											id="email"
											type="email"
											placeholder="Enter your email"
											value={formData.email}
											onChange={(e) =>
												handleInputChange("email", e.target.value)
											}
											className="pl-3 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
										/>
									</div>

									{/* Healthcare Provider */}
									<div>
										<Label
											htmlFor="providerName"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											OBGYN/Midwife Provider
										</Label>
										<div className="relative mt-1">
											<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
											<Input
												ref={providerNameRef}
												id="providerName"
												type="text"
												placeholder="Search for your OBGYN/Midwife provider..."
												value={formData.providerName}
												onChange={(e) =>
													handleInputChange("providerName", e.target.value)
												}
												className="pl-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
										</div>
									</div>

									{/* Due Date */}
									<div>
										<Label
											htmlFor="due-date"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Due Date
										</Label>
										<div className="relative mt-1" ref={datePickerRef}>
											<button
												type="button"
												onClick={() => setShowDatePicker(!showDatePicker)}
												className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary-500 transition-colors z-10"
											>
												<Calendar className="h-5 w-5" />
											</button>
											<div
												onClick={() => setShowDatePicker(!showDatePicker)}
												className="pl-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border border-newTheme-lightGrey rounded-md font-karla"
											>
												<span
													className={
														formData.dueDate ? "text-gray-900" : "text-black"
													}
												>
													{DatePickerButtonValue}
												</span>
											</div>

											{/* Custom Date Picker */}
											{showDatePicker && (
												<div ref={datePickerRef}>
													<DayPicker
														mode="single"
														selected={currentDate}
														onSelect={handleDayPickerSelect}
														showWeekNumber={false}
														showOutsideDays
														classNames={{
															root: `${defaultClassNames.root} absolute top-full left-[-30px] sm:left-0 bg-white border border-newTheme-lightGrey rounded-xl shadow-btn-shadow z-20 p-6 overflow-hidden`,
														}}
														navLayout="around"
														footer={<CalendarFooter onSelect={handleDayPickerSelect} />}
														autoFocus
													/>
												</div>
											)}
										</div>
									</div>

									{/* Password */}
									<div>
										<Label
											htmlFor="password"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Password
										</Label>
										<div className="relative mt-1">
											<Input
												id="password"
												type={showPassword ? "text" : "password"}
												placeholder="Create a password"
												value={formData.password}
												onChange={(e) =>
													handleInputChange("password", e.target.value)
												}
												className="pl-3 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? (
													<EyeOff className="h-4 w-4 text-gray-400" />
												) : (
													<Eye className="h-4 w-4 text-gray-400" />
												)}
											</Button>
										</div>
									</div>

									{/* Confirm Password */}
									<div>
										<Label
											htmlFor="passwordConfirmation"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Confirm Password
										</Label>
										<div className="relative mt-1">
											<Input
												id="passwordConfirmation"
												type={showConfirmPassword ? "text" : "password"}
												placeholder="Confirm your password"
												value={formData.passwordConfirmation}
												onChange={(e) =>
													handleInputChange(
														"passwordConfirmation",
														e.target.value,
													)
												}
												className="pl-3 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
												onClick={() =>
													setShowConfirmPassword(!showConfirmPassword)
												}
											>
												{showConfirmPassword ? (
													<EyeOff className="h-4 w-4 text-gray-400" />
												) : (
													<Eye className="h-4 w-4 text-gray-400" />
												)}
											</Button>
										</div>
									</div>
								</div>

								<Button
									type="submit"
									onClick={handleSignup}
									disabled={loading}
									className="w-full text-black py-[14px] px-[20px] shadow-btn-shadow font-karla text-[20px] h-auto mt-4 bg-newTheme-lime"
								>
									{loading ? (
										<div className="flex items-center">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
											Creating Account...
										</div>
									) : (
										"Create Account"
									)}
								</Button>

								<div className="text-center">
									<p className="text-sm text-black font-karla">
										Already have an account? &nbsp;
										<Link
											to="/login"
											className="text-black hover:text-primary-700 p-0 h-auto font-semibold"
										>
											Sign in
										</Link>
									</p>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Right Column - Image */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="flex justify-center lg:justify-end"
					>
						<div className="relative">
							<img
								src="/Gaia_Hero.png"
								alt="Pregnant woman with doula support"
								className="w-full h-auto max-w-lg object-cover aspect-[4/5]"
							/>
						</div>
					</motion.div>
				</div>
			</div>

		</div>
	);
};

export default ParentSignupPage;
